import { useAuthStore } from '@/stores/auth'
import { useUserSettingsStore } from '@/stores/userSettings'
import { allowedModus } from './apiAllowedModus'
import { showToast } from '../composables/useCustomToast'
import { useUserStore } from '@/stores/userStore'
import type { ErrorResponse, Response } from '@/types/response'
import type { ProjectResponse, Role } from '@/stores/types'
import type { EinloggenInhalt } from '@/types/login'

// TypeScript types and interfaces
type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'
export type ResponseType = 'json' | 'text' | 'blob' | 'arrayBuffer'

interface LoginDaten {
  email?: string
  password?: string
  code?: string
}

interface RequestData {
  Key: string
  Modus: string
  language: string
  LoginDaten?: LoginDaten
  Inhalt?: Record<string, unknown>
}

type RequestParams<TResponseType extends ResponseType = 'json'> = [
  modus: string,
  inhalt?: Record<string, unknown>,
  method?: HttpMethod,
  headersObj?: Record<string, string>,
  responseType?: TResponseType,
  signal?: AbortSignal,
]

export type ReturnResponseFor<
  TResponseType extends ResponseType,
  TData extends Response = Response,
> = TResponseType extends 'json'
  ? TData
  : TResponseType extends 'text'
    ? string
    : TResponseType extends 'blob'
      ? Blob
      : TResponseType extends 'arrayBuffer'
        ? ArrayBuffer
        : never

export class APIService {
  baseUrl: string
  defaultKey: string
  language: string
  isRefreshing: boolean
  queuedRequests: unknown[]

  constructor() {
    this.baseUrl = import.meta.env.VITE_API_URL
    this.defaultKey = ''
    this.language = 'EN'
    this.isRefreshing = false
    this.queuedRequests = []
  }

  // Helper to construct loginData based on modus
  constructLoginData(modus: string, inhalt: Record<string, unknown>): LoginDaten {
    const loginDaten = inhalt.LoginDaten as Record<string, unknown> | undefined
    switch (modus) {
      case 'einloggen':
        return {
          email: typeof loginDaten?.email === 'string' ? loginDaten.email : undefined,
          password: typeof loginDaten?.password === 'string' ? loginDaten.password : undefined,
        }
      case 'rucksetzen':
        return {
          code: typeof loginDaten?.code === 'string' ? loginDaten.code : undefined,
        }
      default:
        return {}
    }
  }

  // Helper to construct requestData
  constructRequestData(
    modus: string,
    inhalt: Record<string, unknown>,
    loginData: LoginDaten,
  ): RequestData {
    const userSettingsStore = useUserSettingsStore()

    const languageStore = userSettingsStore.language
    const baseData: RequestData = {
      Key: this.defaultKey,
      Modus: modus,
      language: languageStore || this.language,
      LoginDaten: loginData,
      Inhalt: inhalt,
    }

    if (modus === 'rucksetzen') {
      // If you expect inhalt.Inhalt to be an object, you can type guard it:
      const inner = inhalt.Inhalt
      baseData.Inhalt =
        inner && typeof inner === 'object' ? (inner as Record<string, unknown>) : undefined
    }

    return baseData
  }

  async request<
    TResponseType extends Exclude<ResponseType, 'json'>,
    TData = ReturnResponseFor<TResponseType>,
  >(...params: RequestParams<TResponseType>): Promise<TData>
  async request<TData extends ReturnResponseFor<'json'>>(
    ...params: RequestParams<'json'>
  ): Promise<TData>
  async request<TResponseType extends ResponseType, TData extends ReturnResponseFor<TResponseType>>(
    ...params: RequestParams<TResponseType>
  ): Promise<TData>
  async request<TResponseType extends ResponseType, TData extends ReturnResponseFor<TResponseType>>(
    modus: string,
    inhalt: Record<string, unknown> = {},
    method: HttpMethod = 'POST',
    headersObj: Record<string, string> = {},
    responseType: TResponseType = 'json' as TResponseType,
    signal?: AbortSignal,
  ) {
    const headers = new Headers()

    if (headersObj && typeof headersObj === 'object') {
      for (const [key, value] of Object.entries(headersObj)) {
        headers.append(key, value)
      }
    }

    const loginData = this.constructLoginData(modus, inhalt)
    const requestData = this.constructRequestData(modus, inhalt, loginData)

    const formData = new FormData()
    formData.append('data', JSON.stringify(requestData))

    const authStore = useAuthStore()
    const userStore = useUserStore()
    if (!authStore.isLoggedIn && !allowedModus.includes(modus)) {
      //User is not logged in Pinia and tries to do an API call
      authStore.piniaLogout()
      throw new Error(`fehler|${modus}|User is not logged in. Cannot make API request.`)
    }

    try {
      const fetchOptions: RequestInit = {
        method,
        headers,
        body: formData,
        credentials: 'include',
        signal,
      }

      const response = await fetch(this.baseUrl, fetchOptions)

      // Handle blob responses (for downloads)
      if (responseType === 'blob') {
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          throw new Error(
            `fehler|${modus}|${(errorData as { Inhalt?: string }).Inhalt || 'Download failed'}`,
          )
        }
        const data: ErrorResponse = await response
          .clone()
          .json()
          .catch(() => ({ status: 'ok' }))
        if (data.Status === 'fehler') {
          throw new Error(`fehler|${modus}|${data.Inhalt || 'Download failed with error response'}`)
        }
        return (await response.blob()) as TData
      }

      // Handle array buffer responses (for downloads)
      if (responseType === 'arrayBuffer') {
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          throw new Error(
            `fehler|${modus}|${(errorData as { Inhalt?: string }).Inhalt || 'Download failed'}`,
          )
        }
        const data: ErrorResponse = await response
          .clone()
          .json()
          .catch(() => ({ status: 'ok' }))
        if (data.Status === 'fehler') {
          throw new Error(`fehler|${modus}|${data.Inhalt || 'Download failed with error response'}`)
        }
        return (await response.blob()) as TData
      }

      const data = (await response.json()) as typeof modus extends 'einloggen'
        ? TData
        : Response<ProjectResponse>

      // If login was successful, update auth store + user settings
      const authStore = useAuthStore()

      if (modus === 'einloggen' && data.Status === 'ok') {
        let roles: Role[] = []

        if (data.Inhalt?.projektList.length == 1) {
          roles = data.Inhalt.projektList[0].rollen ?? []
        }
        authStore.login({
          userID: data.Inhalt.id ? String(data.Inhalt.id) : null,
          email: data.Inhalt?.email || null,
        })
        userStore.updateUser({ roles: roles || [] })
      }

      // Handle token expiration
      if (data.Status === 'expired') {
        // Tries running a Ping function to refresh the token
        try {
          const pingResponse = await this.Ping()
          let pingData: Response

          if (pingResponse) {
            pingData = await pingResponse.json()
          } else {
            throw new Error(`fehler|${modus}|Ping response is undefined`)
          }

          if (pingData.Status === 'ok') {
            // If Ping is ok, it means the Refresh Token is still valid and it reloads the page to avoid any issues with parallel requests
            window.location.reload()
          } else {
            // If Ping is not ok, it means the Refresh Token is no longer valid and the user must really log in again.
            showToast('info', import.meta.env.VITE_EXPIRED_STATUS_MESSAGE)
            console.error('Backend informed session expired. User needs to log in again.')
            console.error(
              'Current authStore state before calling piniaLogout:',
              authStore.isLoggedIn,
            )
            authStore.piniaLogout()
            return responseType === 'text' ? ('' as TData) : (data as TData)
          }
        } catch (error) {
          console.error('Ping request error:', error)
        }
      }
      return data as TData
    } catch (error) {
      if (!signal?.aborted) {
        console.error('API request error:', error)
        throw error
      } else {
        throw new Error(`signal|${modus}|signal aborted`)
      }
    }
  }

  // Login method
  async login(email: string, password: string) {
    const loginData: Record<string, unknown> = {
      Key: this.defaultKey,
      Modus: 'einloggen',
      language: this.language,
      LoginDaten: { email, password },
    }

    return this.request<Response<EinloggenInhalt>>('einloggen', loginData)
  }

  // Reset password method
  async resetPassword(password: string, passwordRepeat: string, code: string) {
    const loginData: Record<string, unknown> = {
      Key: this.defaultKey,
      Modus: 'rucksetzen',
      language: this.language,
      LoginDaten: { code: code },
      Inhalt: { password: password, password2: passwordRepeat },
    }

    return this.request<Response<string>>('rucksetzen', loginData)
  }

  async Ping() {
    console.log('Ping from APIService')
    const requestData = {
      Key: '',
      Modus: 'ping',
      language: 'EN',
      LoginDaten: {
        email: '',
        password: '',
      },
      Inhalt: {},
    }

    try {
      const formData = new FormData()
      formData.append('data', JSON.stringify(requestData))

      const response = await fetch(this.baseUrl, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      })
      return response
    } catch (error) {
      console.error('Ping request error:', error)
    }
  }
}

export const apiInstance = new APIService()
